/** Roving focus over the associations matrix, pure: the table is ONE tab stop and the
 *  arrow keys move between its focus stops (a cell, or the control in it).
 *
 *  Each row lists its stops in column order (`category`, `counts`, the flags, then its
 *  `act:*` buttons — only the ones that can take focus). ←/→ move within the row, ↑/↓ to
 *  the same column of the next row that has stops (else the nearest column by `rank`),
 *  Home/End to the row's ends, Ctrl+Home/End to the grid's. */

export interface NavRow {
  id: string;
  stops: readonly string[];
}

export interface NavPos {
  row: string;
  col: string;
}

/** The action buttons' columns, in the order they sit in the actions cell. */
export const ACTION_COLS = [
  'act:up',
  'act:down',
  'act:revert',
  'act:remove',
  'act:restore',
  'act:override',
] as const;

/** A column's place left to right (`category` 0, `counts` 1, the flags from 2, actions
 *  last), for "the nearest column" when a row lacks the one we come from. */
export function makeRank(flags: readonly string[]): (col: string) => number {
  const order = new Map<string, number>([
    ['category', 0],
    ['counts', 1],
    ...flags.map((f, i) => [f, 2 + i] as [string, number]),
    ...ACTION_COLS.map((a, i) => [a, 2 + flags.length + i] as [string, number]),
  ]);
  return (col) => order.get(col) ?? 0;
}

function nearest(stops: readonly string[], col: string, rank: (c: string) => number): string {
  if (stops.includes(col)) return col;
  let best = stops[0]!;
  for (const s of stops) {
    if (Math.abs(rank(s) - rank(col)) < Math.abs(rank(best) - rank(col))) best = s;
  }
  return best;
}

/** Where a key moves focus from `at` (null: nowhere — the key is not a move, or it would
 *  leave the grid). A position that no longer exists resolves first (`resolve`). */
export function navigate(
  rows: readonly NavRow[],
  at: NavPos,
  key: string,
  opts: { ctrl?: boolean; rank: (col: string) => number },
): NavPos | null {
  const live = rows.filter((r) => r.stops.length > 0);
  const ri = live.findIndex((r) => r.id === at.row);
  if (ri < 0) return null;
  const row = live[ri]!;
  const ci = row.stops.indexOf(at.col);
  if (ci < 0) return null;
  switch (key) {
    case 'ArrowLeft':
      return ci > 0 ? { row: row.id, col: row.stops[ci - 1]! } : null;
    case 'ArrowRight':
      return ci < row.stops.length - 1 ? { row: row.id, col: row.stops[ci + 1]! } : null;
    case 'Home': {
      const r = opts.ctrl ? live[0]! : row;
      return { row: r.id, col: r.stops[0]! };
    }
    case 'End': {
      const r = opts.ctrl ? live[live.length - 1]! : row;
      return { row: r.id, col: r.stops[r.stops.length - 1]! };
    }
    case 'ArrowUp':
    case 'ArrowDown': {
      const target = live[ri + (key === 'ArrowUp' ? -1 : 1)];
      return target ? { row: target.id, col: nearest(target.stops, at.col, opts.rank) } : null;
    }
    default:
      return null;
  }
}

/** A position that exists: `at` itself, else the nearest column of the same row, else the
 *  first stop of `fallbackRow` (or of the grid). */
export function resolve(
  rows: readonly NavRow[],
  at: NavPos | null,
  rank: (col: string) => number,
  fallbackRow?: string,
): NavPos | null {
  const live = rows.filter((r) => r.stops.length > 0);
  if (live.length === 0) return null;
  const row = at ? live.find((r) => r.id === at.row) : undefined;
  if (row && at) return { row: row.id, col: nearest(row.stops, at.col, rank) };
  const fb = live.find((r) => r.id === fallbackRow) ?? live[0]!;
  return { row: fb.id, col: at ? nearest(fb.stops, at.col, rank) : fb.stops[0]! };
}
